import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Badge, MoreCircleOutline as MoreCircle } from '@aslanpay/portal-common-lib';

import { getPointsTransactionReasonLabel } from 'app/constants/pointsTransactions';
import formatNumber from 'app/utils/formatting';
import getPointsValue from 'app/utils/points';
import { formatCurrency } from 'app/utils/strings';
import {
  formatScheduledAt,
  getGiftActions,
  getGiftSourceLabel,
  getGiftStateReasonLabel,
  getGiftStatusDisplay,
  isKnownGiftStatus,
} from 'app/utils/scheduledGifts';

import type { ScheduledGift } from '@aslanpay/portal-common-lib';

export interface ScheduledGiftRowActions {
  onView: (gift: ScheduledGift) => void;
  onEdit: (gift: ScheduledGift) => void;
  onCancel: (gift: ScheduledGift) => void;
  onRetry: (gift: ScheduledGift) => void;
  onSendAgain: (gift: ScheduledGift) => void;
}

/** The list columns the API can sort by, mapped to its sortBy values. */
export const scheduledGiftSortColumns: Record<string, 'scheduledAt' | 'pointsAmount' | 'status'> = {
  scheduledAt: 'scheduledAt',
  pointsAmount: 'pointsAmount',
  status: 'status',
};

/** Actions already under way, so they can't be started twice. */
export interface ScheduledGiftActionsInFlight {
  isRetrying?: boolean;
  isResending?: boolean;
}

export default function getScheduledGiftsColumns(
  actions: ScheduledGiftRowActions,
  { isRetrying = false, isResending = false }: ScheduledGiftActionsInFlight = {}
) {
  const columnHelper = createColumnHelper<ScheduledGift>();
  const viewOnClick = {
    getCellContext: (context: { row: { original: ScheduledGift } }) => ({
      cursor: 'pointer',
      onClick: () => actions.onView(context.row.original),
    }),
  };

  return [
    columnHelper.accessor('scheduledAt', {
      header: 'Send on',
      enableSorting: true,
      meta: viewOnClick,
      cell: (info) => <Text fontWeight="500">{formatScheduledAt(info.getValue())}</Text>,
    }),
    columnHelper.accessor('recipientCount', {
      header: 'Employees',
      enableSorting: false, // the API does not sort by this column
      meta: viewOnClick,
      cell: (info) => formatNumber(info.getValue()),
    }),
    columnHelper.accessor('pointsAmount', {
      header: 'Points per employee',
      enableSorting: true,
      meta: viewOnClick,
      cell: (info) => (
        <Flex direction="column">
          <Text>{formatNumber(info.getValue())} points</Text>
          <Text textStyle="extraSmallMedium" color="brand.darkGray">
            {formatCurrency(getPointsValue(info.getValue()), 'GBP')}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('source', {
      header: 'Source',
      enableSorting: false, // the API does not sort by this column
      meta: viewOnClick,
      cell: (info) => getGiftSourceLabel(info.getValue()),
    }),
    columnHelper.accessor('transactionReason', {
      header: 'Reason',
      enableSorting: false, // the API does not sort by this column
      meta: viewOnClick,
      cell: (info) => getPointsTransactionReasonLabel(info.getValue()),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: true,
      meta: viewOnClick,
      cell: (info) => {
        const { label, badge } = getGiftStatusDisplay(info.getValue());
        const { stateReason } = info.row.original;
        return (
          <Flex direction="column" gap="4px" align="flex-start">
            <Badge size="sm" status={badge} value={label} />
            {stateReason && (
              <Text textStyle="extraSmallMedium" color="brand.darkGray" maxW="220px">
                {getGiftStateReasonLabel(stateReason)}
              </Text>
            )}
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      enableSorting: false,
      meta: {
        pin: 'right',
        customStyles: { justifyContent: 'center' },
        getCellContext: () => ({ style: { padding: 0, textAlign: 'end' } }),
      },
      cell: (info) => {
        const gift = info.row.original;
        const { canEdit, canCancel, canRetry, canSendAgain } = getGiftActions(gift);
        // A status the screen doesn't know gets no actions; the row still opens its details.
        if (isKnownGiftStatus(gift.status)) {
          return (
            <Flex justify="end">
              <Menu autoSelect={false} strategy="fixed">
                <MenuButton
                  as={IconButton}
                  aria-label="Gift options"
                  icon={<MoreCircle boxSize="20px" />}
                  variant="menu"
                  data-testid={`scheduled-gift-menu-${gift.id}`}
                />
                <Portal>
                  <MenuList>
                    <MenuItem onClick={() => actions.onView(gift)}>View details</MenuItem>
                    {canEdit && (
                      <MenuItem
                        onClick={() => actions.onEdit(gift)}
                        data-testid={`edit-scheduled-gift-${gift.id}`}
                      >
                        Edit
                      </MenuItem>
                    )}
                    {canRetry && (
                      <MenuItem
                        isDisabled={isRetrying}
                        onClick={() => actions.onRetry(gift)}
                        data-testid={`retry-scheduled-gift-${gift.id}`}
                      >
                        Retry
                      </MenuItem>
                    )}
                    {canSendAgain && (
                      <MenuItem
                        isDisabled={isResending}
                        onClick={() => actions.onSendAgain(gift)}
                        data-testid={`send-again-scheduled-gift-${gift.id}`}
                      >
                        Send again to failed employees
                      </MenuItem>
                    )}
                    {canCancel && (
                      <MenuItem
                        color="text.error"
                        onClick={() => actions.onCancel(gift)}
                        data-testid={`cancel-scheduled-gift-${gift.id}`}
                      >
                        Cancel gift
                      </MenuItem>
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            </Flex>
          );
        }
        return null;
      },
    }),
  ] as ColumnDef<ScheduledGift, unknown>[];
}
